import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { 
  ArrowRight, CheckCircle2, DollarSign, Link2, Users, Repeat,
  Briefcase, Zap, Clock, ChevronDown, Send, Shield, TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { MarketingNav, MarketingFooter } from "./LandingPage";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.5 } };

export default function PartnerPage() {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", audience: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) { toast.error("Name and email are required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/apply`, formData);
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch {
      // Still show success for now — backend endpoint may not exist yet
      setSubmitted(true);
      toast.success("Application received! We'll be in touch.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter" }}>
      <MarketingNav />

      {/* ═══════ HERO ═══════ */}
      <section className="pt-36 pb-20 md:pt-44 md:pb-28 px-6 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-indigo-600 mb-6">PARTNER PROGRAM</p>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-zinc-950 leading-[1.05] tracking-tighter mb-6" style={{ fontFamily: "Inter" }}>
              Earn recurring revenue by referring clients to HeyIntake
            </h1>
            <p className="text-lg md:text-xl text-zinc-500 leading-relaxed max-w-2xl mx-auto mb-10">
              Help your clients streamline intake and get paid for every referral.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#apply">
                <Button className="h-14 px-8 text-base rounded-full bg-zinc-950 text-white hover:bg-zinc-800 shadow-lg shadow-zinc-300/40 hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="partner-hero-cta">
                  Apply to Partner <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href="#earnings">
                <Button variant="outline" className="h-14 px-8 text-base rounded-full border-zinc-200 text-zinc-700 hover:bg-zinc-50" data-testid="partner-earnings-cta">
                  View Earnings
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ HOW IT WORKS ═══════ */}
      <section className="py-24 px-6 bg-zinc-50">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-16" {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: "Inter" }}>How It Works</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Link2 className="w-7 h-7" />, step: "1", title: "Share your unique referral link", desc: "Get a personalized link to share with your network and clients." },
              { icon: <Users className="w-7 h-7" />, step: "2", title: "Your client signs up and subscribes", desc: "When they subscribe to any paid plan, your referral is tracked automatically." },
              { icon: <DollarSign className="w-7 h-7" />, step: "3", title: "You earn recurring payouts", desc: "Receive monthly payouts for every active referral. Simple as that." }
            ].map((item, i) => (
              <motion.div key={i} className="text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="w-16 h-16 rounded-2xl bg-white border border-zinc-200 shadow-sm flex items-center justify-center mx-auto mb-5 text-indigo-600">
                  {item.icon}
                </div>
                <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2">Step {item.step}</div>
                <h3 className="text-lg font-bold text-zinc-950 mb-2" style={{ fontFamily: "Inter" }}>{item.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ EARNINGS ═══════ */}
      <section id="earnings" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-indigo-600 mb-3">EARNINGS</p>
            <h2 className="text-3xl md:text-5xl font-bold text-zinc-950 tracking-tight mb-3" style={{ fontFamily: "Inter" }}>Earn up to $150 per referral</h2>
            <p className="text-lg text-zinc-500">Recurring commissions on every plan. Monthly or annual.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-10">
            {[
              { plan: "Starter", monthly: "$10/mo for 6 months", annual: "$75 per annual subscription", color: "border-zinc-200", badge: "bg-zinc-100 text-zinc-700" },
              { plan: "Professional", monthly: "$20/mo for 6 months", annual: "$150 per annual subscription", color: "border-indigo-200", badge: "bg-indigo-100 text-indigo-700" }
            ].map((tier, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <Card className={`p-6 rounded-2xl border-2 ${tier.color} h-full`}>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4 ${tier.badge}`}>{tier.plan} Plan</span>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-zinc-400" />
                        <span className="text-sm text-zinc-600">Monthly referral</span>
                      </div>
                      <span className="font-bold text-zinc-950">{tier.monthly}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-zinc-400" />
                        <span className="text-sm text-zinc-600">Annual referral</span>
                      </div>
                      <span className="font-bold text-zinc-950">{tier.annual}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div className="max-w-xl mx-auto text-center" {...fadeUp}>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-zinc-400">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> Payouts issued every 30 days</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Referral must remain active 30 days before payout</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════ WHO IT'S FOR ═══════ */}
      <section className="py-24 px-6 bg-zinc-50">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: "Inter" }}>Who It's For</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { icon: <Briefcase className="w-6 h-6" />, title: "Consultants & Fractional COOs" },
              { icon: <Users className="w-6 h-6" />, title: "Agencies (marketing, ops, legal)" },
              { icon: <Zap className="w-6 h-6" />, title: "Virtual assistants & client managers" },
              { icon: <TrendingUp className="w-6 h-6" />, title: "CRM & automation specialists" }
            ].map((item, i) => (
              <motion.div key={i} className="bg-white border border-zinc-200/60 rounded-2xl p-6 text-center" initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3">{item.icon}</div>
                <p className="text-sm font-medium text-zinc-800">{item.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ WHY PARTNER ═══════ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div className="text-center mb-12" {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: "Inter" }}>Why Partner With HeyIntake</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "Solves a real pain — intake chaos is universal",
              "Easy to explain and sell to any service business",
              "Recurring revenue on every subscription",
              "Fast onboarding — clients see value in minutes",
              "No technical skills required to refer",
              "Dashboard to track all referrals and earnings"
            ].map((item, i) => (
              <motion.div key={i} className="flex items-start gap-3 p-4 bg-zinc-50 rounded-xl" initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}>
                <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-zinc-700 text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ APPLICATION FORM ═══════ */}
      <section id="apply" className="py-24 px-6 bg-zinc-50">
        <div className="max-w-lg mx-auto">
          <motion.div className="text-center mb-10" {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-950 tracking-tight mb-3" style={{ fontFamily: "Inter" }}>Apply to Partner</h2>
            <p className="text-zinc-500">Takes less than a minute. We'll review and get back to you within 48 hours.</p>
          </motion.div>

          {submitted ? (
            <motion.div className="bg-white rounded-2xl border border-zinc-200 p-8 text-center" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-zinc-950 mb-2" style={{ fontFamily: "Inter" }}>Application Received</h3>
              <p className="text-zinc-500">We'll review your application and reach out within 48 hours with your referral link and dashboard access.</p>
            </motion.div>
          ) : (
            <motion.form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-8 space-y-5" {...fadeUp}>
              <div>
                <Label className="text-sm font-medium">Name *</Label>
                <Input placeholder="Your full name" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="mt-1.5" data-testid="partner-name-input" required />
              </div>
              <div>
                <Label className="text-sm font-medium">Email *</Label>
                <Input type="email" placeholder="you@company.com" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} className="mt-1.5" data-testid="partner-email-input" required />
              </div>
              <div>
                <Label className="text-sm font-medium">Company <span className="text-zinc-400 font-normal">(optional)</span></Label>
                <Input placeholder="Your company or agency name" value={formData.company} onChange={(e) => setFormData(p => ({ ...p, company: e.target.value }))} className="mt-1.5" data-testid="partner-company-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Who do you typically work with?</Label>
                <Select value={formData.audience} onValueChange={(v) => setFormData(p => ({ ...p, audience: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="partner-audience-select">
                    <SelectValue placeholder="Select your typical clients..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consultants">Consultants & coaches</SelectItem>
                    <SelectItem value="agencies">Marketing or ops agencies</SelectItem>
                    <SelectItem value="legal">Legal or accounting firms</SelectItem>
                    <SelectItem value="saas">SaaS or tech companies</SelectItem>
                    <SelectItem value="services">Local service businesses</SelectItem>
                    <SelectItem value="mixed">Mixed / various industries</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full h-12 rounded-full bg-zinc-950 text-white hover:bg-zinc-800 text-base font-medium" disabled={submitting} data-testid="partner-submit-btn">
                {submitting ? <span className="flex items-center gap-2"><Clock className="w-4 h-4 animate-spin" /> Submitting...</span> : <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Apply to Partner</span>}
              </Button>
            </motion.form>
          )}
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <motion.div className="text-center mb-10" {...fadeUp}>
            <h2 className="text-3xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: "Inter" }}>Frequently Asked Questions</h2>
          </motion.div>
          <Accordion type="single" collapsible className="space-y-2">
            {[
              { q: "When do I get paid?", a: "Payouts are issued every 30 days for qualifying referrals. A referral qualifies once the client has remained on a paid plan for at least 30 consecutive days." },
              { q: "How do I track referrals?", a: "You'll receive a unique referral link and access to a partner dashboard where you can see clicks, signups, conversions, and earnings in real-time." },
              { q: "Is there a minimum payout?", a: "No. Any amount earned is paid out on the next 30-day cycle." },
              { q: "Can I refer multiple clients?", a: "Yes. There's no limit to the number of clients you can refer. Each qualifying referral earns you commission." },
              { q: "What if my referral cancels?", a: "If a referral cancels before the 30-day qualifying period, no commission is earned. After qualifying, you earn for the duration of their active subscription (up to 6 months for monthly plans)." },
              { q: "Do I need to be a HeyIntake user?", a: "No. Partners don't need to use HeyIntake themselves — though many do to better understand the product." }
            ].map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-zinc-50 border border-zinc-200/60 rounded-xl px-5">
                <AccordionTrigger className="py-4 hover:no-underline text-left">
                  <span className="font-medium text-zinc-900 text-sm">{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent className="pb-4 text-zinc-500 text-sm">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ═══════ BOTTOM CTA ═══════ */}
      <section className="py-20 px-6 bg-zinc-950">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "Inter" }}>Ready to start earning?</h2>
            <p className="text-lg text-zinc-400 mb-8">Apply now and start referring clients within 48 hours.</p>
            <a href="#apply">
              <Button className="h-14 px-10 text-base rounded-full bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20">
                Apply to Partner <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
